/**
 * Record codes: the organization says how its own records are numbered —
 * `CLI26B00001`, `INV/2026/0042`, whatever its paperwork already uses.
 *
 * One kind of record at a time (`Sections`). For each: the pattern, with the
 * tokens a press away; when the number starts again; what it starts from; and,
 * as it is typed, the code the next record would get — asked of the server,
 * which is also what says why a pattern cannot be used.
 *
 * A change is for records made from now on. A code already given was printed
 * on a document and read out, and the page says so before anything is saved.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  FileText,
  FolderKanban,
  Hash,
  ListChecks,
  Receipt,
  RotateCcw,
  Ticket,
  Users,
  Wallet,
} from 'lucide-react';
import {
  Alert,
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  ReadOnly,
  Sections,
  Select,
  Skeleton,
  api,
  toast,
  useCan,
  useDebounced,
} from '@connect/web-shared';

import {
  CODE_PATTERNS_ENDPOINT,
  RESET_OPTIONS,
  TOKENS,
  codePatternKeys,
  useCodePatterns,
  useCodePreview,
  type CodePattern,
  type CodePatternDraft,
  type CodeReset,
} from './record-codes-api';

const iconClass = 'size-4';

// Presentation only: a kind the API adds before this map knows it still shows,
// under the plain mark.
const ICONS: Record<string, ReactNode> = {
  client: <Building2 className={iconClass} />,
  proposal: <FileText className={iconClass} />,
  project: <FolderKanban className={iconClass} />,
  task: <ListChecks className={iconClass} />,
  ticket: <Ticket className={iconClass} />,
  invoice: <Receipt className={iconClass} />,
  payment: <Wallet className={iconClass} />,
  expense: <Receipt className={iconClass} />,
  employee: <Users className={iconClass} />,
};

function draftOf(row: CodePattern): CodePatternDraft {
  return { pattern: row.pattern, reset: row.reset, start: row.start };
}

function PatternEditor({ row, canEdit }: { row: CodePattern; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<CodePatternDraft>(() => draftOf(row));
  const input = useRef<HTMLInputElement>(null);
  // What was saved is what is shown, whenever *it* changes underneath — the
  // saved pattern, not the row. The row is a new object on every refetch, and
  // its "next code" moves each time a record is made: resetting on the row
  // would throw away a pattern half typed whenever a colleague raised an invoice.
  const { pattern, reset, start } = row;
  useEffect(() => setDraft({ pattern, reset, start }), [pattern, reset, start]);

  const changed = draft.pattern !== row.pattern || draft.reset !== row.reset || draft.start !== row.start;
  const asked = useDebounced(draft, 300);
  const preview = useCodePreview(row.entity, asked, changed && asked.pattern.trim().length > 0);
  const waiting = changed && (asked !== draft || preview.isFetching);
  const refused = changed && !waiting && preview.error instanceof ApiError ? preview.error.message : null;

  const save = useMutation({
    mutationFn: (body: CodePatternDraft) => api.put<CodePattern>(`${CODE_PATTERNS_ENDPOINT}/${row.entity}`, body),
    onSuccess: (data) => {
      queryClient.setQueryData<CodePattern[]>(codePatternKeys.all, (rows) =>
        rows?.map((item) => (item.entity === data.entity ? data : item)),
      );
      toast.success('Code pattern saved', `The next one will be ${data.next}.`);
    },
    onError: (error) => toast.error('Not saved', error instanceof ApiError ? error.message : undefined),
  });

  // A token goes in where the cursor is, as if it had been typed there.
  const insert = (token: string) => {
    const field = input.current;
    const at = field?.selectionStart ?? draft.pattern.length;
    const end = field?.selectionEnd ?? at;
    const next = draft.pattern.slice(0, at) + token + draft.pattern.slice(end);
    setDraft((was) => ({ ...was, pattern: next }));
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const next = changed ? preview.data?.next : row.next;
  const then = changed ? preview.data?.then : undefined;
  const isDefault = draft.pattern === row.default_pattern && draft.reset === row.default_reset && draft.start === 1;

  return (
    <ReadOnly when={!canEdit}>
      <div className="grid grid-cols-1 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card>
          <CardHeader title={row.label} description={row.description} />
          <CardBody className="space-y-5">
            <Input
              ref={input}
              label="Code pattern"
              value={draft.pattern}
              maxLength={60}
              spellCheck={false}
              autoComplete="off"
              className="font-mono"
              error={refused ?? undefined}
              hint="Type the fixed parts, and add a token wherever something changes."
              onChange={(event) => setDraft((was) => ({ ...was, pattern: event.target.value }))}
            />
            {canEdit ? (
              <div>
                <p className="pb-2 text-xs font-medium text-muted">Add a token</p>
                <ul className="flex flex-wrap gap-2">
                  {TOKENS.map((item) => (
                    <li key={item.token}>
                      <button
                        type="button"
                        title={`${item.label} — for example ${item.example}`}
                        onClick={() => insert(item.token)}
                        className="flex h-8 items-center gap-2 rounded-control border border-line px-2.5 text-[0.8125rem] transition-colors hover:border-accent hover:bg-accent-subtle focus-visible:border-accent focus-visible:outline-none"
                      >
                        <span className="font-mono text-accent">{item.token}</span>
                        <span className="text-muted">{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Number restarts"
                options={RESET_OPTIONS}
                value={draft.reset}
                onChange={(event) => setDraft((was) => ({ ...was, reset: event.target.value as CodeReset }))}
              />
              <Input
                label="First number"
                inputMode="numeric"
                value={String(draft.start)}
                hint="Raise it to carry on from numbers you already use."
                onChange={(event) =>
                  // Digits only, and a count — never money, so a plain number is right.
                  setDraft((was) => ({
                    ...was,
                    start: Math.max(1, Number(event.target.value.replace(/\D/g, '').slice(0, 9)) || 1),
                  }))
                }
              />
            </div>
            {canEdit ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isDefault}
                  onClick={() => setDraft({ pattern: row.default_pattern, reset: row.default_reset, start: 1 })}
                >
                  <RotateCcw className="size-3.5" />
                  Use the default
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" disabled={!changed} onClick={() => setDraft(draftOf(row))}>
                    Discard
                  </Button>
                  <Button
                    disabled={!changed || waiting || Boolean(refused) || !preview.data}
                    loading={save.isPending}
                    onClick={() => save.mutate({ ...draft, pattern: draft.pattern.trim() })}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
  
        <Card className="2xl:sticky 2xl:top-(--page-sticky-top)">
          <CardHeader title="The next code" description={changed ? 'With what you have typed.' : 'As it is set now.'} />
          <CardBody className="space-y-4">
            <div
              className="flex min-h-20 flex-col items-center justify-center rounded-control bg-accent-subtle px-4 py-5 text-center text-accent"
              aria-live="polite"
            >
              {waiting ? (
                <Skeleton className="h-7 w-40" />
              ) : next ? (
                <>
                  <span className="max-w-full font-mono text-2xl break-all">{next}</span>
                  {then ? <span className="mt-1 font-mono text-xs opacity-80">then {then}</span> : null}
                </>
              ) : (
                <span className="text-sm">Fix the pattern to see it.</span>
              )}
            </div>
            <p className="text-xs leading-relaxed text-muted">
              A change is for records made from now on. A code already given stays as it is — it is on documents that
              have been sent, and it has been read out and written down.
            </p>
          </CardBody>
        </Card>
      </div>
    </ReadOnly>
  );
}

export function RecordCodesPage() {
  const patterns = useCodePatterns();
  const canEdit = useCan('core.code_pattern.edit');

  if (patterns.isLoading) return <LoadingState label="Loading the code patterns" />;
  if (patterns.isError || !patterns.data) {
    return (
      <ErrorState
        message={patterns.error instanceof ApiError ? patterns.error.message : undefined}
        onRetry={() => void patterns.refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Record codes"
        description="How your clients, projects, invoices and other records are numbered. Write them the way your own paperwork already does."
      />
      {canEdit ? null : (
        <Alert tone="info" title="You can see these, not change them">
          Somebody whose role may edit record codes sets how they are written.
        </Alert>
      )}
      {patterns.data.length === 0 ? (
        <EmptyState title="No record codes to set" description="None of the enabled services issues a code." />
      ) : (
        <Sections
          label="Kinds of record"
          param="kind"
          items={patterns.data.map((row) => ({
            id: row.entity,
            label: row.label,
            hint: <span className="font-mono">{row.next}</span>,
            icon: ICONS[row.entity] ?? <Hash className={iconClass} />,
            content: <PatternEditor key={row.entity} row={row} canEdit={canEdit} />,
          }))}
        />
      )}
    </div>
  );
}
